/**
 * DemoResultsPage - Animated demo presentation for workflow results
 *
 * Shows an animated step-by-step presentation of the data transformation process,
 * including CAD to USD currency conversion, date parsing, text cleaning, and AP validation.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import paths from '@/paths';
import {
  usePendingUpload,
  useUpdatePendingUpload,
} from '@/api/spreadsheet/usePendingUploads';
import { useToast } from 'src/context/ToastContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { Card, CardContent } from '@/components/ui/card';
import { AnimatedDemoPresentation } from './components/demo';

const DemoResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams({ strict: false }) as { uploadId?: string };
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the pending upload details
  const { data: uploadData, isLoading } = usePendingUpload(
    uploadId || '',
    !!uploadId,
  );
  const { mutateAsync: updatePendingUpload } = useUpdatePendingUpload();

  const handleSubmit = async () => {
    if (!uploadId) return;

    setIsSubmitting(true);
    try {
      // Mark the pending upload as completed
      await updatePendingUpload({
        id: uploadId,
        status: 'completed',
      });

      // Show success toast
      showToast({
        title: 'Success',
        message:
          'Data submitted successfully! The rebate data has been processed.',
        color: 'success',
        icon: 'check',
      });

      // Navigate back to the email queue
      navigate({ to: paths.authenticated.rebates.uploadDataEmailQueue });
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to submit data. Please try again.',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate({ to: paths.authenticated.rebates.uploadDataEmailQueue });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto py-8 px-4">
          <LoadingSpinner text="Loading results..." minHeight={300} />
        </div>
      </DashboardLayout>
    );
  }

  const fileName = uploadData?.originalFileName || 'lumina_rebate_data.csv';

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="border-l pl-4">
              <h1 className="text-2xl font-bold">Processing: {fileName}</h1>
              <p className="text-muted-foreground text-sm">
                {uploadData?.senderEmail && `From: ${uploadData.senderEmail}`}
                {uploadData?.emailSubject && ` - ${uploadData.emailSubject}`}
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">
                  Automated Data Processing
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  The system will automatically process your file through 6
                  transformation steps: parsing dates, cleaning text, converting
                  CAD to USD, and validating against AP invoices.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Animated Demo Presentation */}
        <AnimatedDemoPresentation
          fileName={fileName}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          autoStart={true}
        />
      </div>
    </DashboardLayout>
  );
};

export default DemoResultsPage;
